import { Helmet } from "react-helmet-async";
import { PageHero } from "@/components/shared/PageHero";
import { ServiceSidebar } from "@/components/services/ServiceSidebar";
import { CTASection } from "@/components/shared/CTASection";
import { ServiceFAQ } from "@/components/services/ServiceFAQ";
import { lawnCareFaqs } from "@/data/serviceFaqs";
import { Leaf, Calendar, Scissors, Clock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import lawnImage from "@/assets/services/seasonal-cleanup.jpg";

const features = [
  {
    icon: Scissors,
    title: "Professional Mowing",
    description: "Precision mowing with professional-grade equipment for a clean, consistent cut every time.",
  },
  {
    icon: Leaf,
    title: "Edging & Trimming",
    description: "Crisp edges along driveways, walkways, and beds for that manicured look.",
  },
  {
    icon: Calendar,
    title: "Weekly Service",
    description: "Reliable weekly scheduling during the growing season keeps your lawn looking its best.",
  },
  {
    icon: Clock,
    title: "Consistent Results",
    description: "The same professional crew each week who know your property and preferences.",
  },
];

const LawnCare = () => {
  return (
    <>
      <Helmet>
        <title>Lawn Care Services | Riekels Lawn Care & Landscaping</title>
        <meta
          name="description"
          content="Professional lawn care and mowing services in the West Twin Cities Metro, MN. Weekly maintenance, edging, and trimming. Call (763) 245-7813 for a free quote."
        />
      </Helmet>
      <main>
        <PageHero
          badge="Our Services"
          title="Lawn Care & Mowing"
          subtitle="Professional lawn maintenance that keeps your property looking its absolute best."
        />

        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="lg:col-span-1">
                <ServiceSidebar />
              </div>
              <div className="lg:col-span-3">
                <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                  <img 
                    src={lawnImage} 
                    alt="Beautifully striped lawn" 
                    className="w-full h-64 md:h-80 object-cover object-center"
                  />
                </div>

                <div className="prose prose-lg max-w-none mb-12">
                  <h2 className="text-3xl font-heading font-bold text-foreground mb-6">
                    Professional Lawn Care for West Metro Homes
                  </h2>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Your lawn is the first thing people notice about your property. At Riekels Lawn Care & Landscaping, 
                    we provide professional mowing and maintenance services that keep your lawn healthy, green, and perfectly manicured.
                  </p>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Our lawn care programs include precision mowing at the optimal height for Minnesota grasses, 
                    crisp edging along all hard surfaces, trimming around obstacles, and thorough cleanup. 
                    We treat every property like it's our own.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {features.map((feature, index) => (
                    <article key={index} className="feature-card">
                      <div className="icon-circle mb-4">
                        <feature.icon className="w-6 h-6" />
                      </div>
                      <h3 className="text-lg font-heading font-bold text-foreground mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground">
                        {feature.description}
                      </p>
                    </article>
                  ))}
                </div>

                <div className="mt-12 bg-secondary rounded-2xl p-8 text-center">
                  <Leaf className="w-12 h-12 mx-auto mb-4 text-secondary-foreground" />
                  <h3 className="text-2xl font-heading font-bold text-secondary-foreground mb-3">
                    Get Your Free Lawn Care Quote
                  </h3>
                  <p className="text-secondary-foreground/80 mb-6 max-w-lg mx-auto">
                    Tell us about your property and we'll provide a customized lawn care plan and pricing.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/contact">
                      <Button size="lg" className="bg-white hover:bg-white/90 text-secondary font-semibold">
                        Request a Quote <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </Link>
                    <a href="https://lawnmammoth.lovable.app/embed/39673b4c-6825-43f1-9697-ad4e1647fba3" target="_blank" rel="noopener noreferrer">
                      <Button size="lg" variant="outline" className="border-white bg-transparent text-white hover:bg-white/10 font-semibold">
                        Get Instant Estimate <ArrowRight className="w-5 h-5 ml-2" />
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <ServiceFAQ faqs={lawnCareFaqs} />

        <CTASection />
      </main>
    </>
  );
};

export default LawnCare;
